import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, ChevronDown, ChevronRight, Save } from 'lucide-react';

const sections = [
{
  id: 'alignment',
  title: '1. Why I\'m Here & How Hard I Push',
  subtitle: 'Alignment before execution',
  questions: [
  { key: 'drives', label: 'What drives me to do this work (be honest)' },
  { key: 'performance', label: 'What "high performance" means to me in practice' },
  { key: 'non_negotiables', label: 'Non-negotiables (values / standards I won\'t bend)' }]

},
{
  id: 'value',
  title: '2. Where I Create Outsized Value',
  subtitle: 'Use me correctly',
  questions: [
  { key: 'strengths', label: 'My top strengths in a team' },
  { key: 'overperform', label: 'Work I consistently overperform at' },
  { key: 'drains', label: 'Work that drains or slows me down' }]

},
{
  id: 'communication',
  title: '3. Communication & Feedback',
  subtitle: 'Default rules — especially under stress',
  questions: [
  { key: 'reach', label: 'Best way to reach me (async / sync / channel)' },
  { key: 'urgent', label: 'What "urgent" means to me' },
  { key: 'feedback', label: 'Feedback I respond best to (be specific)' },
  { key: 'stress', label: 'When I\'m stressed, you\'ll notice I' }]

},
{
  id: 'decisions',
  title: '4. Decisions, Ownership & Accountability',
  subtitle: 'How to move fast with me',
  questions: [
  { key: 'framing', label: 'How I like decisions framed (data / options / principles)' },
  { key: 'input', label: 'When I want input vs when I want to decide' },
  { key: 'ownership', label: 'What "ownership" means to me in practice' },
  { key: 'issues', label: 'How I want issues raised when I drop the ball' }]

},
{
  id: 'conflict',
  title: '5. Conflict, Intensity & Trust',
  subtitle: 'How to challenge me without breaking us',
  questions: [
  { key: 'disagree', label: 'Best way to disagree with me' },
  { key: 'shuts_down', label: 'What shuts me down or escalates things unnecessarily' },
  { key: 'respect', label: 'What earns my respect fast in conflict' }]

},
{
  id: 'energy',
  title: '6. Energy, Pace & Sustainability',
  subtitle: 'How I stay dangerous long-term',
  questions: [
  { key: 'best', label: 'When I\'m at my best (time / conditions)' },
  { key: 'overload', label: 'Signs I\'m overloaded or at risk of burning out' },
  { key: 'reset', label: 'What helps me reset and stay effective' }]

},
{
  id: 'improvement',
  title: '7. How to Make Me Better (Fast)',
  subtitle: 'Compounding improvement',
  questions: [
  { key: 'improve', label: 'Fastest way to help me improve' },
  { key: 'misunderstood', label: 'One thing people often misunderstand about me' },
  { key: 'working_on', label: 'One behavior I\'m actively working to change' }]

},
{
  id: 'scoring',
  title: '8. Self-Scoring',
  subtitle: '1–5 on the following dimensions',
  isScoring: true,
  scoringNote: '1 = Low / Needs protection | 3 = Context dependent | 5 = Thrives here',
  questions: [
    { key: 'intensity', label: 'Intensity Tolerance', description: 'Thrives under pressure & ambiguity' },
    { key: 'ownership', label: 'Ownership Bias', description: 'Defaults to "I own this"' },
    { key: 'speed', label: 'Speed vs Polish', description: 'Comfortable shipping imperfectly' },
    { key: 'directness', label: 'Directness', description: 'Can give & receive hard feedback' },
    { key: 'adaptability', label: 'Adaptability', description: 'Adjusts quickly when reality changes' },
    { key: 'endurance', label: 'Endurance', description: 'Sustains output over long arcs' }
  ]
}];


export default function UserManualSection({ profile, canEdit, onUpdate }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [localManual, setLocalManual] = useState(profile?.user_manual || {});
  const [hasChanges, setHasChanges] = useState(false);

  // Sync localManual with profile changes
  React.useEffect(() => {
    setLocalManual(profile?.user_manual || {});
  }, [profile?.user_manual]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleChange = (sectionId, questionKey, value) => {
    const updatedManual = {
      ...localManual,
      [sectionId]: {
        ...localManual[sectionId],
        [questionKey]: value
      }
    };
    
    setLocalManual(updatedManual);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      console.log('Saving user manual:', localManual);
      await onUpdate({ user_manual: localManual });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving user manual:', error);
      alert('Failed to save: ' + error.message);
    }
  };

  return (
    <>
      <Card className="glass-panel border-none">
        <CardHeader>
          <div>
            <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-400" />
              Officer's Personal User Manual
            </CardTitle>
            <p className="text-xs text-slate-500 mt-2">How to work effectively with me</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {sections.map((section) =>
          <div key={section.id} className="border border-slate-800 rounded-lg overflow-hidden">
              <button
              onClick={() => toggleSection(section.id)} className="bg-slate-100 p-4 w-full flex items-center justify-between hover:bg-slate-900/50 transition-colors">


                <div className="text-left">
                  <div className="text-slate-600 font-semibold">{section.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{section.subtitle}</div>
                </div>
                {expandedSections[section.id] ?
              <ChevronDown className="w-5 h-5 text-slate-400" /> :

              <ChevronRight className="w-5 h-5 text-slate-400" />
              }
              </button>
              
              {expandedSections[section.id] &&
            <div className="bg-slate-100 p-4 space-y-4">
                  {section.isScoring ? (
                    <>
                      <div className="text-xs text-slate-500 italic mb-4 p-3 bg-slate-200 rounded border border-slate-300">
                        {section.scoringNote}
                      </div>
                      {section.questions.map((question) => (
                        <div key={question.key} className="space-y-2 p-3 bg-white rounded-lg border border-slate-300">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <label className="text-sm font-semibold text-slate-700">
                                {question.label}
                              </label>
                              <p className="text-xs text-slate-500 mt-1">{question.description}</p>
                            </div>
                            <div className="text-2xl font-bold text-indigo-600 min-w-[40px] text-center">
                              {localManual[section.id]?.[question.key] || '—'}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((score) => (
                              <button
                                key={score}
                                onClick={() => canEdit && handleChange(section.id, question.key, score)}
                                disabled={!canEdit}
                                className={`flex-1 py-2 rounded-md font-medium transition-all ${
                                  localManual[section.id]?.[question.key] === score
                                    ? 'bg-indigo-600 text-white shadow-md'
                                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                } ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    section.questions.map((question) =>
                      <div key={question.key} className="space-y-2">
                        <label className="text-sm font-medium text-slate-400">
                          {question.label}
                        </label>
                        <Textarea data-testid="profile-usermanualsection-textarea"
                          value={localManual[section.id]?.[question.key] || ''}
                          onChange={(e) => handleChange(section.id, question.key, e.target.value)}
                          placeholder={canEdit ? "Share your thoughts..." : "Not answered yet"} 
                          className="bg-slate-50 text-slate-600 px-3 py-2 text-base rounded-md flex w-full border shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-700 min-h-[80px] resize-none"
                          disabled={!canEdit} 
                        />
                      </div>
                    )
                  )}
                  
                  {canEdit && hasChanges && (
                    <div className="pt-4 border-t border-slate-300">
                      <Button
                        size="sm"
                        onClick={handleSave}
                        className="bg-indigo-600 hover:bg-indigo-700 w-full">
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  )}
                </div>
            }
            </div>
          )}
        </CardContent>
      </Card>
    </>);

}